import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ResetPasswordDraft, ResetPasswordError } from '../domain/reset-password.model';
import { initialState } from './reset-password.state';

/**
 * Reset Password slice. Reducers are pure state transitions only; the submit
 * I/O (the `POST /auth/reset-password` call) lives in the saga, which dispatches
 * these `submit*` transitions as it progresses. This is what lets the reducers
 * be unit-tested as `(state, action) => state` with zero mocks.
 */
const slice = createSlice({
  name: 'resetPassword',
  initialState,
  reducers: {
    /** A single text field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof ResetPasswordDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
    },
    // ── Submit lifecycle (dispatched by the saga) ──
    submitStarted: (state) => {
      state.status = 'submitting';
      state.error = null;
      state.validationErrors = {};
    },
    submitSucceeded: (state) => {
      state.status = 'succeeded';
    },
    submitFailed: (state, action: PayloadAction<ResetPasswordError>) => {
      state.status = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset to the pristine slice (on unmount). */
    resetForm: () => initialState,
  },
});

export const resetPasswordActions = slice.actions;
export const resetPasswordReducer = slice.reducer;
